// HSP Theme Configuration
export const theme = {
  colors: {
    // Primary colors from landing page
    primary: '#FD865C',        // Main orange background
    primaryDark: '#E6754A',    // Darker orange for hover states
    primaryLight: '#FF9A6B',   // Lighter orange for gradients
    
    // Accent colors
    accent: '#FEF3B9',         // Yellow accent (input background)
    accentDark: '#F5E6A3',     // Darker yellow for hover
    
    // Neutral colors
    black: '#000000',          // Primary text and buttons
    white: '#FFFFFF',          // Text on colored backgrounds
    gray: '#666666',           // Secondary text
    lightGray: '#F5F5F5',      // Light backgrounds
    borderGray: '#E8E8E8',     // Borders
    
    // Status colors
    success: '#52C41A',        // Green for success states
    warning: '#FAAD14',        // Yellow for warnings
    error: '#FF4D4F',          // Red for errors
    info: '#1890FF',           // Blue for info
  },
  
  fonts: {
    primary: 'Futura, Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
    secondary: 'Futura, Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
  },
  
  spacing: {
    xs: '4px',
    sm: '8px',
    md: '16px',
    lg: '24px',
    xl: '32px',
    xxl: '48px',
  },
  
  borderRadius: {
    sm: '4px',
    md: '8px',
    lg: '12px',
    xl: '20px',
    round: '50%',
  },
  
  shadows: {
    sm: '0 2px 4px rgba(0,0,0,0.1)',
    md: '0 4px 8px rgba(0,0,0,0.1)',
    lg: '0 8px 16px rgba(0,0,0,0.1)',
    xl: '0 12px 24px rgba(0,0,0,0.15)',
  },
  
  breakpoints: {
    mobile: '768px',
    tablet: '1024px',
    desktop: '1200px',
  }
};

// Helper function to get theme colors
export const getThemeColor = (colorName) => {
  return theme.colors[colorName] || colorName;
};

// Helper function to get theme spacing
export const getThemeSpacing = (size) => {
  return theme.spacing[size] || size;
};

// Helper function to get theme fonts
export const getThemeFont = (fontType = 'primary') => {
  return theme.fonts[fontType];
};

// Common button styles
export const buttonStyles = {
  primary: {
    backgroundColor: theme.colors.black,
    borderColor: theme.colors.black,
    color: theme.colors.white,
    fontFamily: getThemeFont(),
    fontWeight: 'bold',
    borderRadius: theme.borderRadius.md,
    boxShadow: theme.shadows.sm,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.colors.gray,
      borderColor: theme.colors.gray,
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows.md,
    }
  },
  
  secondary: {
    backgroundColor: theme.colors.primary,
    borderColor: theme.colors.primary,
    color: theme.colors.white,
    fontFamily: getThemeFont(),
    fontWeight: 'bold',
    borderRadius: theme.borderRadius.md,
    boxShadow: theme.shadows.sm,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.colors.primaryDark,
      borderColor: theme.colors.primaryDark,
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows.md,
    }
  },
  
  accent: {
    backgroundColor: theme.colors.accent,
    borderColor: theme.colors.accent,
    color: theme.colors.black,
    fontFamily: getThemeFont(),
    fontWeight: 'bold',
    borderRadius: theme.borderRadius.md,
    boxShadow: theme.shadows.sm,
    transition: 'all 0.3s ease',
    '&:hover': {
      backgroundColor: theme.colors.accentDark,
      borderColor: theme.colors.accentDark,
      transform: 'translateY(-2px)',
      boxShadow: theme.shadows.md,
    }
  }
};

// Common input styles
export const inputStyles = {
  primary: {
    backgroundColor: theme.colors.white,
    borderColor: theme.colors.borderGray,
    borderRadius: theme.borderRadius.md,
    fontFamily: getThemeFont(),
    '&:focus': {
      borderColor: theme.colors.primary,
      boxShadow: `0 0 0 2px ${theme.colors.primary}20`,
    }
  },
  
  accent: {
    backgroundColor: theme.colors.accent,
    borderColor: theme.colors.borderGray,
    borderRadius: theme.borderRadius.md,
    fontFamily: getThemeFont(),
    '&:focus': {
      borderColor: theme.colors.primary,
      boxShadow: `0 0 0 2px ${theme.colors.primary}20`,
    }
  }
};

// Common card styles
export const cardStyles = {
  primary: {
    backgroundColor: theme.colors.white,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.md,
    border: `1px solid ${theme.colors.borderGray}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows.lg,
      transform: 'translateY(-2px)',
    }
  },
  
  accent: {
    backgroundColor: theme.colors.accent,
    borderRadius: theme.borderRadius.lg,
    boxShadow: theme.shadows.md,
    border: `1px solid ${theme.colors.borderGray}`,
    transition: 'all 0.3s ease',
    '&:hover': {
      boxShadow: theme.shadows.lg,
      transform: 'translateY(-2px)',
    }
  }
};

export default theme;
